import { pick } from 'lodash-es';
import { PathVariables } from 'services/types';
import {
  MutationEndpointFactoryConfig,
  ServiceEndpointFactoryConfig,
} from './types';

/**
 * Replaces all path variables in a string.
 *
 * For example, given a path of '/asset/:id' and a `variables` object of
 * { id: 1 }, this function will return '/asset/1'
 */
export function replacePathVariables<TPath extends string>(
  path: TPath,
  variables: PathVariables<TPath>,
): string {
  return Object.keys(variables).reduce((currentPath, variable) => {
    const pathVariable = `:${variable}`;
    const value = variables[variable as keyof PathVariables<TPath>];
    return currentPath.replace(pathVariable, String(value));
  }, path);
}

/**
 * Using `pathVarNames`, this function extracts all of the entries with matching
 * keys from the `args` object.
 */
export function getPathVariablesFromArgs<
  TPath extends string,
  TArgs,
  TQueryResult = unknown,
  TResult = TQueryResult,
>(
  pathVarNames: Array<keyof PathVariables<TPath>>,
  config:
    | ServiceEndpointFactoryConfig<TPath, TArgs, TQueryResult, TResult>
    | undefined,
  args: TArgs,
) {
  if (pathVarNames.length === 0) {
    return undefined;
  }

  const { pathVariables } = config ?? {};

  if (pathVariables) {
    return pathVariables(args);
  }

  if (typeof args === 'object') {
    return pick(args, pathVarNames) as unknown as PathVariables<TPath>;
  }

  return args as PathVariables<TPath>;
}

export function getQueryParamsFromArgs<
  TPath extends string,
  TArgs,
  TQueryResult = unknown,
  TResult = TQueryResult,
>(
  config:
    | ServiceEndpointFactoryConfig<TPath, TArgs, TQueryResult, TResult>
    | undefined,
  args: TArgs,
) {
  const { queryParams } = config ?? {};

  if (Array.isArray(queryParams)) {
    return pick(args, queryParams);
  }

  if (typeof queryParams === 'function') {
    return queryParams(args);
  }

  // return args as QueryParams;
  return undefined;
}

export function getDataFromArgs<
  TPath extends string,
  TArgs = unknown,
  TQueryResult = unknown,
  TResult = TQueryResult,
  TData extends object | undefined = undefined,
>(
  config:
    | MutationEndpointFactoryConfig<TPath, TArgs, TData, TQueryResult, TResult>
    | undefined,
  args: TArgs,
) {
  const { data } = config ?? {};

  if (data && typeof data === 'function') {
    return data(args);
  }

  if (Array.isArray(data)) {
    return pick(args, data) as TData;
  }

  return args as unknown as TData;
}

export function returnVoid() {
  return (): void => {};
}
